import { createContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import useAxios from "../../utils/useAxios";
import { resObj } from "../../utils/constants";

const PayloadPhaseContext = createContext();

export default PayloadPhaseContext;

export const PayloadPhaseProvider = ({ children }) => {
  const api = useAxios();
  const navigate = useNavigate();

  const initValues = {
    bufferAcronymArray: [],
    operatorArray: [],
    bufferUniqueIdArray: [],
    bufferConcentration: '',
    targetPH: '',
    encapsulantBufferAcronym: '',
    payloadNameArray: [],
    payloadTypeArray: [],
    reporterArray: [],
    payloadBatchNumberArray: [],
  };
  const [initialValues, setInitialValues] = useState(initValues);

  const payloadPhaseObj = {
    payloadName: '',
    payloadType: '',
    reporter: '',
    payloadBatchNumber: '',
    payloadPhaseUniqueId: '',
    encapsulantBufferId: null, 
    concentration: '', 
    operatorArray: [],
  };
  const [payloadPhaseData, setPayloadPhaseData] = useState(payloadPhaseObj);
  const [apiRes, setApiRes] = useState(resObj);
  const [formErrors, setFormErrors] = useState({});
  const [isSubmit, setIsSubmit] = useState(false);
  const [payloadPhaseId, setPayloadPhaseId] = useState('');

  useEffect(() => {
    getInitialData();
    getOperators();
  }, []);

  const getInitialData = async () => {
    try {
      setApiRes({...apiRes, loading: true});
      const response = await api.get(
        '/api/materials-capture/payload-phase/initial/data/get'
      );
      
      if (response.status === 200) {
        setInitialValues(prev => ({
          ...prev,
          bufferAcronymArray: response.data.buffer_acronym_list,
          bufferUniqueIdArray: response.data.buffer_unique_id_list,
          payloadNameArray: response.data.payload_name_list,
          payloadTypeArray: response.data.payload_type_list,
          reporterArray: response.data.reporter_list,
          payloadBatchNumberArray: response.data.payload_batch_number_list,
        }));
        setApiRes({ ...apiRes, loading: false });
      }
    } catch (err){
      setApiRes({
        ...apiRes,
        axiosError: true,
        errMsg: JSON.stringify(err.response.data).replace(/[{}[\]"']/g, ''),
        errHeading: 'Payload Phase Init Data Get',
        loading: false,
      });
    }
  };

  const getOperators = async () => {
    try {
      setApiRes({...apiRes, loading: true});
      const response = await api.get('/api/materials-capture/operators/get');
      
      if (response.status === 200) {        
        setInitialValues(prev => ({
          ...prev,
          operatorArray: response.data,
        }))
        setApiRes({ ...apiRes, loading: false });
      }
    } catch (err){
      setApiRes({
        ...apiRes,
        axiosError: true,
        errMsg: JSON.stringify(err.response.data).replace(/[{}[\]"']/g, ''),
        errHeading: 'Operators Get',
        loading: false,
      });
    }
  };

  const validate = () => {
    const errors = {};

    if (!payloadPhaseData?.payloadName) {
      errors['payloadName'] = 'Payload Name is required!';
    }
    if (!payloadPhaseData?.payloadType) {
      errors['payloadType'] = 'Payload Type is required!';
    }
    if (!payloadPhaseData?.reporter) {
      errors['reporter'] = 'Reporter is required!';
    }
    if (!payloadPhaseData?.payloadBatchNumber) {
      errors['payloadBatchNumber'] = 'Payload Batch Number is required!';
    }
    if (!payloadPhaseData?.payloadPhaseUniqueId) {
      errors['payloadPhaseUniqueId'] = 'Payload Phase Unique ID is required!';
    }
    if (!payloadPhaseData?.encapsulantBufferId) {
      errors['encapsulantBufferId'] = 'Encapsulant Buffer unique ID is required!';
    }
    if (!payloadPhaseData?.concentration) {
      errors['concentration'] = 'Concentration is required!';
    }
    if (payloadPhaseData?.operatorArray.length === 0) {
      errors['operator'] = 'Operator is required!';
    }

    return errors;
  };

  const handleSubmit = () => {
    setFormErrors(validate());
    setIsSubmit(true);
  };

  useEffect(() => {    
    if (Object.keys(formErrors).length === 0 && isSubmit) {
      submitPayloadPhase();
    }
  }, [formErrors]);

  const submitPayloadPhase = () => {
    if (payloadPhaseId === '') {
      createPayloadPhase();
    } else {
      updatePayloadPhase();
    }
  };

  const createPayloadPhase = async () => {
    try {
      setApiRes({...apiRes, loading: true});

      const response = await api.post(
        '/api/materials-capture/payload-phase/create', {
        payload_name: payloadPhaseData?.payloadName,
        payload_type: payloadPhaseData?.payloadType,
        reporter: payloadPhaseData?.reporter,
        payload_batch_number: payloadPhaseData?.payloadBatchNumber,
        payload_phase_unique_id: payloadPhaseData?.payloadPhaseUniqueId,
        encapsulant_buffer_id: payloadPhaseData?.encapsulantBufferId,
        concentration: payloadPhaseData?.concentration,
        operator_list: payloadPhaseData?.operatorArray,
      });

      if (response.status === 200) {
        setApiRes({
          ...apiRes,
          showAlert: true,
          successMsg: response.data,
          loading: false,
        });
        navigate('/payload-phase');
      }
    } catch (err) {
      setApiRes({
        ...apiRes,
        axiosError: true,
        errMsg: JSON.stringify(err.response.data).replace(/[{}[\]"']/g, ''),
        errHeading: 'Payload Phase create',
        loading: false,
      });
    }
  };

  const updatePayloadPhase = async () => {
    try {
      setApiRes({...apiRes, loading: true});

      const response = await api.post('/api/materials-capture/payload-phase/edit', {
        payload_phase_id: payloadPhaseId,
        payload_name: payloadPhaseData?.payloadName,
        payload_type: payloadPhaseData?.payloadType,
        reporter: payloadPhaseData?.reporter,
        payload_batch_number: payloadPhaseData?.payloadBatchNumber,
        payload_phase_unique_id: payloadPhaseData?.payloadPhaseUniqueId,
        encapsulant_buffer_id: payloadPhaseData?.encapsulantBufferId,
        concentration: payloadPhaseData?.concentration,
        operator_list: payloadPhaseData?.operatorArray,
      });

      if (response.status === 200) {
        setApiRes({
          ...apiRes,
          showAlert: true,
          successMsg: response.data,
          loading: false,
        });
      }
    } catch (err) {
      setApiRes({
        ...apiRes,
        axiosError: true,
        errMsg: JSON.stringify(err.response.data).replace(/[{}[\]"']/g, ''),
        errHeading: 'Payload Phases update',
        loading: false,
      });
    }
  };

  useEffect(() => {
    if (payloadPhaseId !== '') getPayloadPhaseDetails()
  }, [payloadPhaseId]);

  useEffect(() => {
    if (payloadPhaseData?.encapsulantBufferId !== null) getBufferData()
  }, [payloadPhaseData?.encapsulantBufferId]);

  const getBufferData = async () => {
    try {
      setApiRes({...apiRes, loading: true});
            
      const response = await api.get(
        '/api/materials-capture/payload-phase/buffer/data/get', {
          params: {
            buffer_id: payloadPhaseData?.encapsulantBufferId
          }
        });
      
      if (response.status === 200) {
        setInitialValues(prev => ({
          ...prev,
          bufferConcentration: response.data.buffer_concentration,
          targetPH: response.data.target_pH,
          encapsulantBufferAcronym: response.data.buffer_acronym,
        }));
        setApiRes({ ...apiRes, loading: false });
      }
    } catch (err){
      setApiRes({
        ...apiRes,
        axiosError: true,
        errMsg: JSON.stringify(err.response.data).replace(/[{}[\]"']/g, ''),
        errHeading: 'Payload Phase Buffer Data Get',
        loading: false,
      });
    }
  };

  const getPayloadPhaseDetails = async () => {
    try {
      setApiRes({...apiRes, loading: true});
      const response = await api.get(
        '/api/materials-capture/payload-phase/details/get', {
          params: {
            payload_phase_id: payloadPhaseId
          }
        });
      
      if (response.status === 200) {
        const payloadPhaseData = response.data;        
        setPayloadPhaseData(prev => ({
          ...prev,
          payloadName: payloadPhaseData.payload_name,
          payloadType: payloadPhaseData.payload_type,
          reporter: payloadPhaseData.reporter,
          payloadBatchNumber: payloadPhaseData.payload_batch_number,
          payloadPhaseUniqueId: payloadPhaseData.payload_phase_unique_id,
          encapsulantBufferId: payloadPhaseData.encapsulant_buffer_unique_id,
          concentration: payloadPhaseData.concentration,
          operatorArray: payloadPhaseData.operator_list,
          encapsulantBufferAcronym: payloadPhaseData.encapsulant_buffer_acronym,
        }));
        setApiRes({ ...apiRes, loading: false });
      }
    } catch (err){
      setApiRes({
        ...apiRes,
        axiosError: true,
        errMsg: JSON.stringify(err.response.data).replace(/[{}[\]"']/g, ''),
        errHeading: 'Payload Phase Details Get',
        loading: false,
      });
    }
  };

  const contextData = {
    initialValues: initialValues,
    payloadPhaseData: payloadPhaseData,
    setPayloadPhaseData: setPayloadPhaseData,
    formErrors: formErrors,
    handleSubmit: handleSubmit,
    apiRes: apiRes,
    setApiRes: setApiRes,
    setPayloadPhaseId: setPayloadPhaseId,
  };

  return (
    <PayloadPhaseContext.Provider value={contextData}>
      {children}
    </PayloadPhaseContext.Provider>
  )
};